/* eslint-disable max-lines */
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import useRoles from 'common/hooks/useRoles';
import PatientBanner from 'common/modules/PatientBanner';
import { isEmpty, map } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import SelectField from 'common/forms/SelectField';
import useInfoDialog from 'common/hooks/useInfoDialog';
import TransferResultsModal from 'common/modules/TransferResultsModal';
import { selectTransferDestinations } from 'common/utils/facilityEligibility';
import {
  TRANSFER_STATUS,
  buildTransferResultRows,
  shouldShowTransferResultsDialog,
  summarizeTransferOutcomes,
} from 'common/utils/transferOutcomes';
import { transferOrderToFacility } from 'store/thunks/pharmacyThunks';

const TransferOrderButton = ({
  orderId,
  mpi,
  orderFacilityId,
  orderIds,
  lineItems,
  onBatchTransferComplete,
  showLineItemSelection,
  sendLineItemIdsOnly,
  disabled,
  disabledTooltip,
  patientData,
}) => {
  const dispatch = useDispatch();
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const orderIdsToTransfer = orderIds || [orderId];
  const selectableLineItems = (lineItems || []).filter(
    ({ lineItemId, processingState }) =>
      !!lineItemId && !['SPLIT', 'CANCELED', 'SHIPPED'].includes(processingState)
  );
  const hasMultipleLineItems = selectableLineItems.length > 1;
  const shouldSelectLineItems = hasMultipleLineItems && showLineItemSelection;

  const currentTenantId = useSelector(({ auth }) => auth?.currentTenantId);
  const fulfillingPharmacies = useSelector(({ pharmacy }) => pharmacy[currentTenantId]);

  // POM-5906/5907: dispensing facilities only, still excluding the order's own facility. The
  // current-facility banner below deliberately resolves against the UNFILTERED list, so an order
  // sitting at a 503A facility still shows where it is today.
  const transferFacilities = selectTransferDestinations(fulfillingPharmacies, {
    excludeFacilityId: orderFacilityId,
  });
  const currentFacility = fulfillingPharmacies?.find(
    ({ fulfillingPharmacyId }) => fulfillingPharmacyId === orderFacilityId
  );

  const hasAccess = useRoles();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLineItemIds, setSelectedLineItemIds] = useState([]);
  // Non-null while the post-transfer results dialog is up. Holds the rows plus the counts it needs.
  const [transferResults, setTransferResults] = useState(null);

  // Dismissing the results dialog is what completes the refresh — but only when something actually
  // moved. A fully blocked batch leaves the view untouched so the operator can retry.
  const handleResultsClose = () => {
    const didTransfer = (transferResults?.totalTransferred ?? 0) > 0;
    setTransferResults(null);

    if (didTransfer) {
      onBatchTransferComplete();
    }
  };

  const handleOpenModal = () => {
    if (shouldSelectLineItems) {
      setSelectedLineItemIds(map(selectableLineItems, 'lineItemId'));
    }
    setShowModal(true);
  };
  const handleCloseModal = (_, reason) => {
    /* istanbul ignore next */
    if (reason !== 'backdropClick') {
      setShowModal(false);
    }
  };

  const toggleLineItemSelection = (lineItemId) => {
    setSelectedLineItemIds((currentSelectedLineItemIds) => {
      if (currentSelectedLineItemIds.includes(lineItemId)) {
        return currentSelectedLineItemIds.filter(
          (selectedLineItemId) => selectedLineItemId !== lineItemId
        );
      }

      return [...currentSelectedLineItemIds, lineItemId];
    });
  };

  const triggerTransferOrderApi = ({ toFacility, lineItemIds }) => {
    const selectedIdsForTransfer = lineItemIds || [];
    const selectableLineItemIds = map(selectableLineItems, 'lineItemId');
    const isFullEligibleSelection =
      !isEmpty(selectableLineItemIds) &&
      selectedIdsForTransfer.length === selectableLineItemIds.length &&
      selectedIdsForTransfer.every((lineItemId) => selectableLineItemIds.includes(lineItemId));

    let transferScopePayload;
    if (sendLineItemIdsOnly) {
      transferScopePayload = { lineItemIds: selectedIdsForTransfer };
    } else if (isFullEligibleSelection) {
      transferScopePayload = { orderIds: orderIdsToTransfer };
    } else {
      transferScopePayload = { lineItemIds: selectedIdsForTransfer };
    }

    const payload = {
      mpi,
      fulfillingPharmacyId: toFacility.fulfillingPharmacyId,
      ...transferScopePayload,
    };

    setIsLoading(true);
    dispatch(transferOrderToFacility(payload))
      .then((response) => {
        // POM-5945: the old check was `if (isTransferred)` on the response ARRAY, and a non-empty
        // array is truthy — so a fully blocked transfer reported success. Branch on the counts.
        const { status, totalTransferred, totalRequested, reasonSummary } =
          summarizeTransferOutcomes(response);

        // The request itself failed; handleError has already raised the error dialog.
        if (status === TRANSFER_STATUS.ERROR) return;

        // A partial result, or a fully blocked multi-order batch, needs per-order attribution that a
        // snackbar cannot carry. Cleanup is deferred to the results dialog's dismissal so the
        // operator can read the failures before the view refreshes.
        if (shouldShowTransferResultsDialog({ status, totalRequested })) {
          setShowModal(false);
          setTransferResults({
            rows: buildTransferResultRows(response.outcomes),
            totalRequested,
            totalTransferred,
            destinationLabel: `${toFacility.name} - ${toFacility.state}`,
          });

          return;
        }

        if (status === TRANSFER_STATUS.NONE) {
          // Previously this produced NO feedback at all. Surface the backend's reason and leave the
          // dialog open so the operator can pick a different facility.
          enqueueSnackbar(
            reasonSummary
              ? `Transfer not allowed: ${reasonSummary}`
              : 'Transfer not allowed for the selected order(s).',
            { variant: 'error', autoHideDuration: 4000 }
          );

          return;
        }

        // Unchanged all-success UX. PARTIAL no longer reaches here — it always routes to the
        // results dialog above, since a partial result means at least two orders were requested.
        enqueueSnackbar(`Order Transferred to ${toFacility.name} - ${toFacility.state}`, {
          variant: 'success',
        });

        setShowModal(false);
        onBatchTransferComplete();
      })
      .finally(() => setIsLoading(false));
  };

  const handleTransferInitiation = (formData) => {
    const { fulfillingPharmacy: toFacility } = formData;
    const lineItemIdsForTransfer = shouldSelectLineItems
      ? selectedLineItemIds
      : map(selectableLineItems, 'lineItemId');

    if (hasMultipleLineItems) {
      showInfoDialog({
        title: 'Confirm Facility Transfer',
        message: (
          <Box>
            <Typography variant='body2' sx={{ mb: 1 }}>
              You are about to transfer selected line item(s) to{' '}
              <strong>
                {toFacility.name} - {toFacility.state}
              </strong>
              .
            </Typography>
            <Typography variant='body2' component='div'>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>
                  If the order contains only one line item, the entire order will be transferred to
                  the selected facility.
                </li>
                <li>
                  If the order contains multiple line items and you select one or more specific line
                  items, the selected item(s) will be split into a new order and transferred.
                </li>
              </ul>
            </Typography>
            <Typography variant='body2' sx={{ mt: 1 }}>
              Do you want to continue?
            </Typography>
          </Box>
        ),
        callback: () => {
          triggerTransferOrderApi({
            toFacility,
            lineItemIds: lineItemIdsForTransfer,
          });
        },
        cancelable: true,
        confirmButtonText: 'Yes, Transfer',
      });

      return;
    }

    showInfoDialog({
      title: 'Confirm Transfer',
      message: `Are you sure you want to transfer this order to facility ${toFacility.name} - ${toFacility.state}?`,
      callback: () => {
        triggerTransferOrderApi({
          toFacility,
          lineItemIds: map(selectableLineItems, 'lineItemId'),
        });
      },
      cancelable: true,
      confirmButtonText: 'Transfer',
    });
  };

  return (
    <>
      <InfoDialog />

      {transferResults && (
        <TransferResultsModal
          open
          onClose={handleResultsClose}
          rows={transferResults.rows}
          totalRequested={transferResults.totalRequested}
          totalTransferred={transferResults.totalTransferred}
          destinationLabel={transferResults.destinationLabel}
        />
      )}

      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        aria-describedby='transferOrderHeader-header'
        fullWidth
      >
        <Grid container sx={{ px: 3, py: 2 }} justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' component='h2' id='transferOrderHeader-header'>
            Transfer Order To Facility {orderId ? `- ${orderId}` : ''}
          </Typography>
        </Grid>
        <DialogContent>
          <Form
            onSubmit={handleTransferInitiation}
            render={({ handleSubmit, invalid }) => (
              <form onSubmit={handleSubmit} noValidate>
                {mpi && (
                  <Box sx={{ mb: 2 }}>
                    <PatientBanner
                      mpi={mpi}
                      firstName={patientData?.firstName}
                      lastName={patientData?.lastName}
                      dob={patientData?.dob}
                      email={patientData?.email}
                      gender={patientData?.gender}
                    />
                  </Box>
                )}
                {currentFacility && (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant='body2'>
                        Current Facility:{' '}
                        <strong>
                          {currentFacility.name} - {currentFacility.state}
                        </strong>
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />
                  </>
                )}

                {shouldSelectLineItems && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant='subtitle2' sx={{ mb: 1 }}>
                      Select Line Item(s) to Transfer
                    </Typography>
                    <FormGroup>
                      {map(selectableLineItems, (lineItem) => (
                        <FormControlLabel
                          key={lineItem.lineItemId}
                          control={
                            <Checkbox
                              checked={selectedLineItemIds.includes(lineItem.lineItemId)}
                              onChange={() => toggleLineItemSelection(lineItem.lineItemId)}
                            />
                          }
                          label={`${lineItem.itemName || lineItem.lineItemId} ${
                            lineItem.rxNumber ? `(RX: ${lineItem.rxNumber})` : ''
                          }`}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                )}

                <SelectField
                  id='Transfer-facility-dropdown'
                  label='Select Facility To Initiate Transfer'
                  name='fulfillingPharmacy'
                  required
                >
                  {map(transferFacilities, (facility) => (
                    <MenuItem value={facility} key={facility.fulfillingPharmacyId}>
                      {facility.name} - {facility.state}
                    </MenuItem>
                  ))}
                </SelectField>

                <Grid container justifyContent='flex-end' sx={{ mt: 5 }}>
                  <Button
                    variant='outlined'
                    color='secondary'
                    onClick={handleCloseModal}
                    sx={{ mr: 2 }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    variant='contained'
                    type='submit'
                    disabled={invalid || (shouldSelectLineItems && isEmpty(selectedLineItemIds))}
                    loading={isLoading}
                  >
                    Confirm
                  </LoadingButton>
                </Grid>
              </form>
            )}
          />
        </DialogContent>
      </Dialog>

      <Tooltip arrow placement='top' title={disabled && disabledTooltip ? disabledTooltip : ''}>
        <span>
          <Button
            size='small'
            color='secondary'
            variant='outlined'
            onClick={handleOpenModal}
            disabled={!hasAccess.transferOrder || disabled}
            sx={{
              px: 1,
              fontSize: '0.75rem',
              minHeight: '30px',
              lineHeight: 'normal',
            }}
          >
            Transfer Facility
          </Button>
        </span>
      </Tooltip>
    </>
  );
};

TransferOrderButton.propTypes = {
  orderId: PropTypes.string,
  mpi: PropTypes.string,
  orderFacilityId: PropTypes.string.isRequired,
  orderIds: PropTypes.arrayOf(PropTypes.string),
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      lineItemId: PropTypes.string.isRequired,
      itemName: PropTypes.string,
      processingState: PropTypes.string,
      rxNumber: PropTypes.string,
    })
  ),
  onBatchTransferComplete: PropTypes.func,
  showLineItemSelection: PropTypes.bool,
  sendLineItemIdsOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
  patientData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dob: PropTypes.string,
    email: PropTypes.string,
    gender: PropTypes.string,
  }),
};

TransferOrderButton.defaultProps = {
  orderId: null,
  orderIds: null,
  lineItems: [],
  mpi: null,
  onBatchTransferComplete: () => {},
  showLineItemSelection: true,
  sendLineItemIdsOnly: false,
  disabled: false,
  disabledTooltip: '',
  patientData: null,
};

export default TransferOrderButton;
